import apiConfig from "../configs/apiConfig";
import camelKeys from "camelcase-keys";
import groupBy from "../util/groupByUtil";
import { getTimestampInLocalTime } from "../util/dateUtil";
import { axiosInstance } from "../infrastructure/http";
import { AxiosResponse } from "axios";
import { CustomerOrderDetails, FormattedCustomerOrder, InventoryDetail } from "../common/CustomerOrder";
import { CreditRequestReversalRequest } from "@/common/api/Models/CreditRequestReversalRequest";

export async function fetchReceivePurchaseOrderDetails(
	purchaseOrderNumber: string,
	invoiceNumber?: string,
	id?: string,
	includeComments = false,
	includeDraftReceive = false
): Promise<AxiosResponse<any>> {
	const query: string[] = [];
	if (invoiceNumber) query.push(`invoiceNumber=` + invoiceNumber);
	if (id) query.push(`receiveId=` + id);
	if (includeComments) query.push(`includeComments=true`);
	if (includeDraftReceive) query.push(`includeDraftReceive=true`);
	let queryString = query.join("&");

	return new Promise((resolve, reject) => {
		axiosInstance
			.get(
				`${apiConfig.ReceivingUrl}/ReceivePurchaseOrderDetails?purchaseOrderNumber=${purchaseOrderNumber}${
					queryString ? "&" + queryString : ""
				}`
			)
			.then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export function getReceiveById(receiveId: string): Promise<AxiosResponse<any>> {
	return new Promise((resolve, reject) => {
		axiosInstance
			.get(`${apiConfig.ReceiveUrl}/Receive/${receiveId}`, {
				headers: {
					"Content-Type": "application/json",
					"Ocp-Apim-Subscription-Key": apiConfig.JbhApimKey,
				},
			})
			.then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export function upsertReceive(receiveObj: any): Promise<AxiosResponse<any>> {
	receiveObj.receiveLineItems = receiveObj.receiveLineItems.map((li: any) => {
		return { ...li, modifiedDate: new Date().toISOString() };
	});
	return new Promise((resolve, reject) => {
		axiosInstance
			.post(`${apiConfig.ReceiveUrl}/Receive`, receiveObj, {
				headers: {
					"Ocp-Apim-Subscription-Key": apiConfig.JbhApimKey,
				},
			})
			.then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export function postReceiveFinalise(receiveObj: any): Promise<AxiosResponse<any>> {
	receiveObj.receiveLineItems = receiveObj.receiveLineItems.map((li: any) => {
		return { ...li, modifiedDate: new Date().toISOString() };
	});
	return new Promise((resolve, reject) => {
		axiosInstance
			.post(`${apiConfig.ReceiveUrl}/Receive`, receiveObj, {
				headers: {
					"Content-Type": "application/json",
					"Ocp-Apim-Subscription-Key": apiConfig.JbhApimKey,
					"x-finalise": "true",
				},
			})
			.then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export function postCreditRequest(creditRequest: any): Promise<AxiosResponse<any>> {
	return new Promise((resolve, reject) => {
		axiosInstance
			.post(`${apiConfig.ReceivingUrl}/CreditRequest`, creditRequest, {
				headers: {
					"Content-Type": "application/json",
				},
			})
			.then((response) => {
				resolve(camelKeys(response, { deep: true }) as AxiosResponse<any>);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export function getCreditRequest(Id: string): Promise<any | null> {
	return new Promise((resolve, reject) => {
		axiosInstance
			.get(`${apiConfig.ReceivingUrl}/CreditRequestDetails/${Id}`, {
				headers: {
					"Content-Type": "application/json"
				},
			})
			.then((response) => {
				resolve(camelKeys(response.data));
			})
			.catch((error) => {
				if (error.response === undefined || error.response.status !== 404) {
					reject(error);
				} else {
					resolve(null);
				}
			});
	});
}

export function getCreditRequestsByStatusesAndStoreNumber(
	statusesArray: string[],
	locationId: string | number
): Promise<any | null> {
	const statusesString = statusesArray.join(",");
	let queryString = `${apiConfig.ReceivingUrl}/CreditRequests?statuses=${statusesString}`;
	if (String(locationId) !== "1") {
		queryString = `${queryString}&locationId=${locationId}`;
	}
	return new Promise((resolve, reject) => {
		axiosInstance
			.get(queryString, {
				headers: {
					"Content-Type": "application/json"
				},
			})
			.then((response) => {
				resolve(camelKeys(response.data, { deep: true }));
			})
			.catch((error) => {
				if (error.response === undefined || error.response.status !== 404) {
					reject(error);
				} else {
					resolve(null);
				}
			});
	});
}

export function getValidateInvoiceNumberSyntax(vendorId: string, invoiceNumberSyntax: string): Promise<any> {
	return new Promise((resolve, reject) => {
		axiosInstance
			.get(
				`${apiConfig.VendorUrl}/ValidateInvoiceNumberSyntax?SourceId=${vendorId}&InvoiceNumber=${invoiceNumberSyntax}&SourceSystem=Shopkeeper`,
				{
					headers: {
						"Content-Type": "application/json",
						"Ocp-Apim-Subscription-Key": apiConfig.JbhApimKey,
					},
					timeout: 5000,
				}
			)
			.then((response) => {
				resolve(camelKeys(response.data, { deep: true }));
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export async function getIfInvoiceNumberExistsAsync(
	invoiceNumber: string,
	vendorId: string
): Promise<AxiosResponse<any>> {
	return new Promise((resolve, reject) => {
		axiosInstance
			.get(`${apiConfig.ReceivingUrl}/Invoice/Existing?invoiceNumber=${invoiceNumber}&accountNumber=${vendorId}`)
			.then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export async function getCustomerOrderV3Orders(
	lineItems: { sku: string; quantity: number }[],
	locationId: string | number
): Promise<AxiosResponse<any>> {
	const searchRequest = {
		lineItems: lineItems.filter((item) => item.quantity > 0),
		fulfilmentLocationId: locationId,
	};

	if (
		searchRequest.lineItems.length === 0 ||
		searchRequest.lineItems.every((item) => !item.sku || item.sku.trim().length === 0)
	) {
		// Some components will set a default empty SKU until they have real SKUs loaded from the PO.
		return Promise.resolve({ data: [] } as AxiosResponse<any>);
	}
	return new Promise((resolve, reject) => {
		axiosInstance
			.post(`${apiConfig.ReceivingUrl}/Orders/Search`, searchRequest)
			.then((response) => {
				resolve(camelKeys(response, { deep: true }) as AxiosResponse<any>);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export async function getCustomerOrdersAsync(
	lineItems: { sku: string; quantity: number }[],
	locationId: string | number
): Promise<CustomerOrderDetails> {
	const apiV3CustomerOrders = await getCustomerOrderV3Orders(lineItems, locationId);
	let formattedApiCustomerOrders: FormattedCustomerOrder[] = [];
	apiV3CustomerOrders.data.forEach((order: any) => {
		formattedApiCustomerOrders = [
			...formattedApiCustomerOrders,
			...order.lineItems.map((lineItem: any): FormattedCustomerOrder => {
				return {
					orderId: order.orderId,
					orderNumber: order.orderNumber,
					lineItemId: lineItem.id,
					sku: lineItem.sku,
					quantity: lineItem.quantity,
					quantityAllocated: lineItem.allocatedQuantity,
					dateCreated: getTimestampInLocalTime(order.dateCreated),
					sortTimestamp: order.dateCreated,
					source: "COV3",
					fulfilmentType: lineItem.fulfilmentType,
					customerPhone: order.customerMobileNumber,
					customerName: order.customerName,
					customerId: "",
					status: lineItem.status,
				};
			}),
		];
	});

	const groupedOrders = groupBy(formattedApiCustomerOrders, "sku");
	return {
		orders: formattedApiCustomerOrders,
		inventory: Object.keys(groupedOrders).map((key): InventoryDetail => {
			return {
				sku: key,
				quantity: groupedOrders[key].reduce((sum: number, currentValue: FormattedCustomerOrder) => {
					return sum + currentValue.quantity - currentValue.quantityAllocated;
				}, 0),
			};
		}),
	};
}

export async function getLabelConfigBySkusAsync(skuList: string[]): Promise<AxiosResponse<any>> {
	return new Promise((resolve, reject) => {
		axiosInstance
			.post(`${apiConfig.ReceivingUrl}/labels/product-label-config`, skuList)
			.then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export function upsertCreditRequest(creditRequestObj: any): Promise<AxiosResponse<any>> {
	return new Promise((resolve, reject) => {
		axiosInstance
			.post(`${apiConfig.ReceivingUrl}/CreditRequest`, creditRequestObj, {
				headers: {
					"Content-Type": "application/json"
				},
			})
			.then((response) => {
				resolve(camelKeys(response, { deep: true }) as AxiosResponse<any>);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export async function searchAsync(searchTerm: string, userLocationId: string | number): Promise<AxiosResponse<any>> {
	return new Promise((resolve, reject) => {
		axiosInstance
			.get(`${apiConfig.ReceivingUrl}/Search?q=${searchTerm}&locationId=${userLocationId}`)
			.then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export async function asnSearchAsync(searchTerm: string, userLocationId: string | number): Promise<AxiosResponse<any>> {
	return new Promise((resolve, reject) => {
		axiosInstance
			.get(`${apiConfig.ReceivingUrl}/Asn/Search?q=${searchTerm}&locationId=${userLocationId}`)
			.then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export async function postReopenPurchaseOrderAsync(purchaseOrderNumber: string): Promise<AxiosResponse<any>> {
	return new Promise((resolve, reject) => {
		axiosInstance
			.post(`${apiConfig.ReceivingUrl}/PurchaseOrder/${purchaseOrderNumber}/Reopen`, null)
			.then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

// works exactly as upsertReceive but targetting BFF endpoint and accepts optional arrival payload
export async function postReceiveAsync(receive: any): Promise<AxiosResponse<any>> {
	receive.receiveLineItems = receive.receiveLineItems.map((li: any) => {
		return { ...li, modifiedDate: new Date().toISOString() };
	});
	return new Promise((resolve, reject) => {
		axiosInstance
			.post(`${apiConfig.ReceivingUrl}/Receive`, receive)
			.then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export async function postSubmitReceiveAsync(
	receiveObj: any,
	customerOrders: any[],
	printerId: string,
	selectedLabelPrinterId: string,
	locationId: string | number,
	toBeFinalised: boolean,
	hasLabels: boolean,
	skipAutoOrderAllocation: boolean,
	skipProductLabelPrinting: boolean
): Promise<AxiosResponse<any>> {
	const submitReceiveRequest = {
		receive: receiveObj,
		orders: customerOrders,
		printerId,
		labelPrinterId: selectedLabelPrinterId,
		locationId,
		localTimestamp: getTimestampInLocalTime(new Date()),
		toBeFinalised,
		hasLabels,
		skipAutoOrderAllocation,
		skipProductLabelPrinting,
	};

	submitReceiveRequest.receive.receiveLineItems = submitReceiveRequest.receive.receiveLineItems.map((li: any) => {
		return { ...li, modifiedDate: new Date().toISOString() };
	});

	return new Promise((resolve, reject) => {
		axiosInstance
			.post(`${apiConfig.ReceivingUrl}/Receive/Submit`, submitReceiveRequest)
			.then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			});
	});
}
/**
 * Handles the line item received event (when user is scanning in stock)
 *
 * @param {LineItemReceivedEvent} lineItem - The line item being received.
 */

export async function postLineItemReceivedAsync(lineItem: any): Promise<AxiosResponse<any>> {
	return new Promise((resolve, reject) => {
		axiosInstance
			.post(`${apiConfig.ReceivingUrl}/line-item/received`, lineItem)
			.then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export async function getAsnBySsccAsync(sscc: string): Promise<AxiosResponse<any>> {
	return new Promise((resolve, reject) => {
		axiosInstance
			.get(`${apiConfig.ReceivingUrl}/Asn/SSCC/${sscc}`)
			.then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export async function postMultipleReceivesAsync(receives: any[]): Promise<AxiosResponse<any>> {
	return new Promise((resolve, reject) => {
		axiosInstance
			.post(`${apiConfig.ReceivingUrl}/Receive/Multiple`, receives)
			.then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export async function postReceiveComment(receiveId: string, comment: any): Promise<AxiosResponse<any>> {
	return new Promise((resolve, reject) => {
		axiosInstance
			.post(`${apiConfig.ReceivingUrl}/receive/${receiveId}/comments`, comment)
			.then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export async function getInitialCreditData(poNumber: string, invNumber?: string): Promise<AxiosResponse<any>> {
	const reqPath = invNumber
		? `InitialCreditData?purchaseOrderNumber=${poNumber}&invoiceNumber=${invNumber}`
		: `InitialCreditData?purchaseOrderNumber=${poNumber}`;
	return new Promise((resolve, reject) => {
		axiosInstance
			.get(`${apiConfig.ReceivingUrl}/${reqPath}`)
			.then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export async function postAdhocCreditRequestAsync(adhocCreditRequest: any): Promise<AxiosResponse<any>> {
	return new Promise((resolve, reject) => {
		axiosInstance
			.post(`${apiConfig.ReceivingUrl}/AdhocCreditRequest`, adhocCreditRequest)
			.then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export async function postLabelRequests(
	printerId: string,
	labelRequests: any[],
	locationId: string | number,
	printerOutputType?: string,
	startColumn?: number,
	startRow?: number,
	selectedFormat?: string
): Promise<AxiosResponse<any>> {
	return new Promise((resolve, reject) => {
		axiosInstance
			.post(`${apiConfig.ReceivingUrl}/Labels/Products`, {
				printerId,
				locationId,
				products: labelRequests,
				...(printerOutputType && { printerOutputType }),
				...(startColumn !== undefined && { startColumn }),
				...(startRow !== undefined && { startRow }),
				...(selectedFormat && { selectedFormat }),
			})
			.then(resolve)
			.catch(reject);
	});
}

export async function postTagSheetLabelRequests(
	printerId: string,
	from: number,
	to: number,
	locationId: string | number,
	printerOutputType?: string,
	startColumn?: number,
	startRow?: number,
	selectedFormat?: string,
	description?: string
): Promise<AxiosResponse<any>> {
	return new Promise((resolve, reject) => {
		axiosInstance
			.post(`${apiConfig.ReceivingUrl}/Labels/TagSheets`, {
				printerId,
				locationId,
				from,
				to,
				...(printerOutputType && { printerOutputType }),
				...(startColumn !== undefined && { startColumn }),
				...(startRow !== undefined && { startRow }),
				...(selectedFormat && { selectedFormat }),
				...(description && { description }),
			})
			.then(resolve)
			.catch(reject);
	});
}

export async function getLabelConfigurations(locationId?: number | string): Promise<AxiosResponse<any>> {
	const params = locationId !== undefined ? `?locationId=${locationId}` : "";
	return axiosInstance.get(`${apiConfig.ReceivingUrl}/Labels/Configurations${params}`);
}

export async function getLabelProductsByType(
	documentNumber: string,
	type: "PurchaseOrder" | "Transfer"
): Promise<AxiosResponse<any>> {
	return axiosInstance.get(
		`${apiConfig.ReceivingUrl}/Labels/Products?Type=${type}&documentNumber=${encodeURIComponent(documentNumber)}`
	);
}

export async function searchProduct(searchTerm: string): Promise<AxiosResponse<any>> {
	return new Promise((resolve, reject) => {
		axiosInstance
			.get(`${apiConfig.ReceivingUrl}/Products/Search?q=${searchTerm}`)
			.then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export async function searchCreditRequests(searchTerm: string, userLocation: number): Promise<AxiosResponse<any>> {
	var query = searchTerm.toUpperCase().includes("JBCR-") ? 
		`?friendlyId=${searchTerm.toUpperCase()}` : 
		`?purchaseOrderNumber=${searchTerm}`

	if (userLocation !== 1) query += `&locationId=${userLocation}`
	
	return new Promise((resolve, reject) => {
		axiosInstance
			.get(`${apiConfig.ReceivingUrl}/CreditRequests${query}`)
			.then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export async function postCreditReversal(reversal: CreditRequestReversalRequest, id: string): Promise<AxiosResponse<any>> {
	return new Promise((resolve, reject) => {
		axiosInstance
			.post(
				`${apiConfig.ReceivingUrl}/CreditRequestReversal/${id}`,
				reversal
			).then((response) => {
				resolve(response);
			})
			.catch((error) => {
				reject(error);
			})
	})
}
